import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { catalogService } from '../../../services/catalogService';

const CatalogItemsSection = ({ selectedItems, onItemsChange, discount, onDiscountChange, error }) => {
  const [catalogItems, setCatalogItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCatalogItemId, setSelectedCatalogItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCatalogItems();
  }, []);

  useEffect(() => {
    // Filter items based on search term
    if (searchTerm?.trim() === '') {
      setFilteredItems(catalogItems);
    } else {
      const search = searchTerm?.toLowerCase();
      const filtered = catalogItems?.filter(item => 
        item?.name?.toLowerCase()?.includes(search) || 
        item?.category?.toLowerCase()?.includes(search)
      );
      setFilteredItems(filtered);
    }
  }, [searchTerm, catalogItems]);

  const loadCatalogItems = async () => {
    try {
      const items = await catalogService?.getAll();
      setCatalogItems(items || []);
      setFilteredItems(items || []);
    } catch (err) {
      console.error('Error loading catalog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!selectedCatalogItemId) {
      alert('Veuillez sélectionner un article');
      return;
    }

    const catalogItem = catalogItems?.find(item => item?.id === selectedCatalogItemId);
    if (!catalogItem) return;

    const existingItem = selectedItems?.find(item => item?.catalogItemId === catalogItem?.id);
    
    if (existingItem) {
      const updatedItems = selectedItems?.map(item =>
        item?.catalogItemId === catalogItem?.id
          ? { 
              ...item, 
              quantity: item?.quantity + quantity, 
              priceTotal: (item?.quantity + quantity) * item?.priceUnit 
            }
          : item
      );
      onItemsChange(updatedItems);
    } else {
      const newItem = {
        catalogItemId: catalogItem?.id,
        catalogItemName: catalogItem?.name,
        catalogItemCategory: catalogItem?.category,
        quantity: quantity,
        priceUnit: catalogItem?.price,
        priceTotal: quantity * catalogItem?.price
      };
      onItemsChange([...selectedItems, newItem]);
    }

    // Reset selection
    setSelectedCatalogItemId('');
    setQuantity(1);
  };

  const handleRemoveItem = (catalogItemId) => {
    const updatedItems = selectedItems?.filter(item => item?.catalogItemId !== catalogItemId);
    onItemsChange(updatedItems);
  };

  const getTotalAmount = () => {
    return selectedItems?.reduce((sum, item) => sum + (item?.priceTotal || 0), 0);
  };

  // ✅ NEW: Calculate total with discount
  const getTotalBeforeDiscount = () => {
    return getTotalAmount();
  };

  const getTotalAfterDiscount = () => {
    const total = getTotalAmount();
    const discountAmount = discount || 0;
    return Math.max(total - discountAmount, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Bar + Dropdown + Quantity + Add Button */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Search Input */}
        <div className="md:col-span-12">
          <label className="block text-sm font-medium text-foreground mb-2">
            Rechercher un article
          </label>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              placeholder="Tapez pour rechercher par nom ou catégorie (ex: barrique, TV, matelas...)"
              className="w-full px-4 py-3 pl-10 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-card"
            />
            <Icon 
              name="Search" 
              size={20} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
          </div>
          {searchTerm && (
            <p className="text-sm text-muted-foreground mt-1">
              {filteredItems?.length} article(s) trouvé(s)
            </p>
          )}
        </div>

        {/* Dropdown for selecting articles */}
        <div className="md:col-span-7">
          <label className="block text-sm font-medium text-foreground mb-2">
            Sélectionner un article <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedCatalogItemId}
            onChange={(e) => setSelectedCatalogItemId(e?.target?.value)}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-card"
          >
            <option value="">-- Choisir un article --</option>
            {filteredItems?.map(item => (
              <option key={item?.id} value={item?.id}>
                {item?.name} - {item?.price?.toFixed(2)} € ({item?.category})
              </option>
            ))}
          </select>
        </div>

        {/* Quantity Input */}
        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-foreground mb-2">
            Quantité <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e?.target?.value) || 1)}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Add Button */}
        <div className="md:col-span-2 flex items-end">
          <button
            type="button"
            onClick={handleAddItem}
            disabled={!selectedCatalogItemId}
            className="w-full py-3 px-4 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="Plus" size={20} />
            <span>Ajouter</span>
          </button>
        </div>
      </div>

      {/* Selected Items Table */}
      {selectedItems?.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="bg-accent/10 px-4 py-3 border-b border-border">
            <h4 className="font-medium text-foreground flex items-center space-x-2">
              <Icon name="Package" size={20} className="text-primary" />
              <span>Articles ajoutés</span>
            </h4>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-accent/5 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Nom de l'article</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Catégorie</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Prix unitaire</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-foreground">Quantité</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Total ligne</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-foreground">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {selectedItems?.map(item => (
                  <tr key={item?.catalogItemId} className="hover:bg-accent/5">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{item?.catalogItemName}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{item?.catalogItemCategory}</td>
                    <td className="px-4 py-3 text-sm text-right text-foreground">{item?.priceUnit?.toFixed(2)} €</td>
                    <td className="px-4 py-3 text-sm text-center font-medium text-foreground">{item?.quantity}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-foreground">{item?.priceTotal?.toFixed(2)} €</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item?.catalogItemId)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Supprimer"
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-accent/5 border-t border-border">
                <tr>
                  <td colSpan="4" className="px-4 py-3 text-right text-sm font-medium text-foreground">
                    Total avant remise:
                  </td>
                  <td className="px-4 py-3 text-right text-base font-bold text-foreground">
                    {getTotalBeforeDiscount()?.toFixed(2)} €
                  </td>
                  <td></td>
                </tr>
                <tr className="border-t border-border">
                  <td colSpan="4" className="px-4 py-3">
                    <div className="flex justify-end items-center space-x-2">
                      <label className="text-sm font-medium text-foreground">
                        Remise (€):
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={discount || 0}
                        onChange={(e) => onDiscountChange(Number(e?.target?.value) || 0)}
                        className="w-32 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="0"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-base font-bold text-red-600">
                    {discount > 0 ? `-${discount?.toFixed(2)} €` : '0.00 €'}
                  </td>
                  <td></td>
                </tr>
                <tr className="border-t-2 border-border">
                  <td colSpan="4" className="px-4 py-3 text-right text-base font-bold text-foreground">
                    Total à payer:
                  </td>
                  <td className="px-4 py-3 text-right text-xl font-bold text-primary">
                    {getTotalAfterDiscount()?.toFixed(2)} €
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 mt-2">{error}</p>
      )}
    </div>
  );
};

export default CatalogItemsSection;